import React, { useState, useRef, useLayoutEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import ReactDOMServer from 'react-dom/server';
import SchemaIcon from '@mui/icons-material/Schema';

// Inject fonts directly into this file
const fontStyles = `
  /* ======================== */
  /* LOAD MYRIAD PRO (LOCAL) */
  /* ======================== */
  @font-face {
    font-family: 'Myriad Pro';
    src: url('/fonts/MyriadPro.woff2') format('woff2');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }

  /* ======================== */
  /* LOAD RALEWAY (GOOGLE)   */
  /* ======================== */
  @import url('https://fonts.googleapis.com/css2?family=Raleway:wght@300;400;500;600&display=swap');

  /* ======================== */
  /* APPLY FONT GLOBALS       */
  /* ======================== */

  h1, h2, h3, h4, h5, h6 {
    font-family: 'Myriad Pro', sans-serif !important;
  }

  body, p, span, div, button, input, label {
    font-family: 'Raleway', sans-serif !important;
  }
`;

const LandingPage = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [openTestimonials, setOpenTestimonials] = useState(false);
  const containerRef = useRef(null);
  const textBlockRef = useRef(null);
  const [breakPoint, setBreakPoint] = useState(600);
  const sfTimerRef = useRef(null);
  const vaultTimerRef = useRef(null);
  const sfCurrentLoadedRef = useRef(false);
  const sfNextReadyRef = useRef(false);
  const sfTokenRef = useRef(0);
  const vaultCurrentLoadedRef = useRef(false);
  const vaultNextReadyRef = useRef(false);
  const vaultTokenRef = useRef(0);
  const AUTO_ADVANCE_MS = 5000;

  const sfImages = [
    `${process.env.PUBLIC_URL}/images/studioflow_on_devices_1.jpg`,
    `${process.env.PUBLIC_URL}/images/studioflow_on_devices_2.png`,
    `${process.env.PUBLIC_URL}/images/studioflow_on_devices_3.png`,
  ];

  const vaultImages = [
    `${process.env.PUBLIC_URL}/images/studiovault_on_devices_1.jpg`,
    `${process.env.PUBLIC_URL}/images/studiovault_on_devices_2.png`,
  ];

  const [sfIndex, setSfIndex] = useState(0);
  const [vaultIndex, setVaultIndex] = useState(0);

  const changeImage = (newIndex) => {
    if (sfTimerRef.current) clearTimeout(sfTimerRef.current);
    const img = new Image();
    img.src = sfImages[newIndex];
    img.onload = () => setSfIndex(newIndex);
  };

  const nextImage = () => changeImage((sfIndex + 1) % sfImages.length);
  const prevImage = () => changeImage((sfIndex - 1 + sfImages.length) % sfImages.length);

  const changeVaultImage = (newIndex) => {
    if (vaultTimerRef.current) clearTimeout(vaultTimerRef.current);
    const img = new Image();
    img.src = vaultImages[newIndex];
    img.onload = () => setVaultIndex(newIndex);
  };

  const nextVaultImage = () => changeVaultImage((vaultIndex + 1) % vaultImages.length);
  const prevVaultImage = () => changeVaultImage((vaultIndex - 1 + vaultImages.length) % vaultImages.length);

  const maybeScheduleSfAdvance = useCallback(() => {
    if (!sfCurrentLoadedRef.current || !sfNextReadyRef.current) return;
    if (sfTimerRef.current) clearTimeout(sfTimerRef.current);
    sfTimerRef.current = setTimeout(nextImage, AUTO_ADVANCE_MS);
  }, [nextImage]);

  const maybeScheduleVaultAdvance = useCallback(() => {
    if (!vaultCurrentLoadedRef.current || !vaultNextReadyRef.current) return;
    if (vaultTimerRef.current) clearTimeout(vaultTimerRef.current);
    vaultTimerRef.current = setTimeout(nextVaultImage, AUTO_ADVANCE_MS);
  }, [nextVaultImage]);

  const preloadSfNext = useCallback(() => {
    const token = ++sfTokenRef.current;
    const nextIndex = (sfIndex + 1) % sfImages.length;
    const img = new Image();
    img.src = sfImages[nextIndex];
    img.onload = () => {
      if (sfTokenRef.current !== token) return;
      sfNextReadyRef.current = true;
      maybeScheduleSfAdvance();
    };
  }, [sfIndex, sfImages, maybeScheduleSfAdvance]);

  const preloadVaultNext = useCallback(() => {
    const token = ++vaultTokenRef.current;
    const nextIndex = (vaultIndex + 1) % vaultImages.length;
    const img = new Image();
    img.src = vaultImages[nextIndex];
    img.onload = () => {
      if (vaultTokenRef.current !== token) return;
      vaultNextReadyRef.current = true;
      maybeScheduleVaultAdvance();
    };
  }, [vaultIndex, vaultImages, maybeScheduleVaultAdvance]);

  React.useEffect(() => {
    sfCurrentLoadedRef.current = false;
    sfNextReadyRef.current = false;
    if (sfTimerRef.current) clearTimeout(sfTimerRef.current);
    preloadSfNext();
  }, [sfIndex, preloadSfNext]);

  React.useEffect(() => {
    vaultCurrentLoadedRef.current = false;
    vaultNextReadyRef.current = false;
    if (vaultTimerRef.current) clearTimeout(vaultTimerRef.current);
    preloadVaultNext();
  }, [vaultIndex, preloadVaultNext]);

  React.useEffect(() => {
    return () => {
      if (sfTimerRef.current) clearTimeout(sfTimerRef.current);
      if (vaultTimerRef.current) clearTimeout(vaultTimerRef.current);
    };
  }, []);

  const updateBreak = useCallback(() => {
    if (!containerRef.current || !textBlockRef.current) return;
    const containerTop = containerRef.current.getBoundingClientRect().top + window.scrollY;
    const textBottom = textBlockRef.current.getBoundingClientRect().bottom + window.scrollY;
    const offset = Math.max(200, Math.round(textBottom - containerTop + 24));
    setBreakPoint(offset);
  }, []);

  useLayoutEffect(() => {
    updateBreak();
    window.addEventListener('resize', updateBreak);
    return () => window.removeEventListener('resize', updateBreak);
  }, [updateBreak]);

  React.useEffect(() => {
    window.addEventListener('load', updateBreak);
    const timeoutId = setTimeout(updateBreak, 150);
    return () => {
      window.removeEventListener('load', updateBreak);
      clearTimeout(timeoutId);
    };
  }, [updateBreak]);

  const handleDialogOpen = () => setOpenDialog(true);
  const handleDialogClose = () => setOpenDialog(false);
  const handleTestimonialsOpen = () => setOpenTestimonials(true);
  const handleTestimonialsClose = () => setOpenTestimonials(false);

  const schemaIconSvg = encodeURIComponent(
    ReactDOMServer.renderToStaticMarkup(<SchemaIcon />)
  );

  return (
    <>
      {/* Inject font styles */}
      <style>{fontStyles}</style>

      <Helmet>
        <title>StudioFlow</title>
        <link rel="icon" href={`data:image/svg+xml,${schemaIconSvg}`} />
      </Helmet>

      {/* BLUE GRADIENT BACKGROUND */}
      <Box
        ref={containerRef}
        sx={{
          minHeight: '100vh',
          backgroundImage: `linear-gradient(to bottom, #ffffff 0%, #e3f2fd 12%, #b3e5fc 30%, #70cafa 55%, #1e3c72 100%), linear-gradient(to bottom, #ffffff 0%, #e3f2fd 12%, #b3e5fc 30%, #70cafa 55%, #1e3c72 100%)`,
          backgroundSize: `100% ${breakPoint}px, 100% calc(100% - ${breakPoint}px)`,
          backgroundPosition: `0 0, 0 ${breakPoint}px`,
          backgroundRepeat: 'no-repeat',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
          py: 4
        }}
      >

        {/* LOGO */}
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            mb: 2,
            zIndex: 2,
          }}
        >
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL}/images/StudioFlow long_Color.png`}
            alt="StudioFlow Logo"
            sx={{
              width: '100%',
              maxWidth: '900px',
              height: 'auto'
            }}
          />
        </Box>

        {/* Animated Glow */}
        <motion.div
          initial={{ scale: 1.2, opacity: 0.3 }}
          animate={{ scale: 1, opacity: 0.5 }}
          transition={{ duration: 6, repeat: Infinity, repeatType: 'reverse' }}
          style={{
            position: 'absolute',
            top: '-20%',
            left: '-20%',
            width: '140%',
            height: '140%',
            background:
              'radial-gradient(circle, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 70%)',
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          
          {/* PRODUCT CARDS SECTION */}
          <Grid container spacing={4} justifyContent="center" alignItems="stretch" sx={{ mb: 2 }}>
            
            {/* StudioFlow Card */}
            <Grid item xs={12} md={5} sx={{ display: 'flex' }}>
              <Card
                sx={{
                  flex: 1,
                  background: 'linear-gradient(135deg, #2a5298 0%, #1e3c72 100%)',
                  borderRadius: '20px',
                  color: '#fff',
                  boxShadow: 6,
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative'
                }}
              >
                <CardContent sx={{ textAlign: 'center', p: 4, flexGrow: 1 }}>

                  {/* CONTACT US BUTTON */}
                  <Button
                    variant="contained"
                    onClick={handleDialogOpen}
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 32,
                      px: 2.5,
                      py: 1,
                      fontSize: '0.95rem',
                      background: '#ffffff33',
                      backdropFilter: 'blur(4px)',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontFamily: 'Raleway',
                      '&:hover': { background: '#ffffff55' }
                    }}
                  >
                    Contact Us
                  </Button>

                  {/* IMAGE CAROUSEL */}
                  <Box sx={{ position: 'relative', width: '100%', mt: 6 }}>
                    <Button
                      onClick={prevImage}
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: 0,
                        transform: 'translateY(-50%)',
                        minWidth: '40px',
                        color: '#fff',
                        fontSize: '2rem'
                      }}
                    >
                      ‹
                    </Button>

                    <motion.img
                      key={sfIndex}
                      src={sfImages[sfIndex]}
                      alt="StudioFlow Screenshot"
                      onLoad={() => {
                        updateBreak();
                        sfCurrentLoadedRef.current = true;
                        maybeScheduleSfAdvance();
                      }}
                      style={{
                        width: '100%',
                        borderRadius: '12px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
                      }}
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5 }}
                    />

                    <Button
                      onClick={nextImage}
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        right: 0,
                        transform: 'translateY(-50%)',
                        minWidth: '40px',
                        color: '#fff',
                        fontSize: '2rem'
                      }}
                    >
                      ›
                    </Button>
                  </Box>

                </CardContent>
              </Card>
            </Grid>

            {/* Workflow Text Between Cards */}
            <Grid
              item
              xs={12}
              md={2}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center'
              }}
            >
              <Typography
                ref={textBlockRef}
                variant="h6"
                sx={{
                  color: '#fff',
                  lineHeight: 1.5,
                  textAlign: 'center'
                }}
              >
                A complete and customizable workflow built for transparency<br />
                that helps teams provide consistency and deliver consistenly on time.
              </Typography>
              <Box
                component="img"
                src={`${process.env.PUBLIC_URL}/images/StudioVault_long_Color.png`}
                alt="StudioVault Logo"
                sx={{
                  width: '100%',
                  maxWidth: '900px',
                  height: 'auto',
                  mt: 3
                }}
              />
            </Grid>

            {/* StudioVault Card */}
            <Grid item xs={12} md={5} sx={{ display: 'flex' }}>
              <Card
                sx={{
                  flex: 1,
                  background: 'linear-gradient(135deg, #2a5298 0%, #1e3c72 100%)',
                  borderRadius: '20px',
                  color: '#fff',
                  boxShadow: 6,
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative'
                }}
              >
                <CardContent sx={{ textAlign: 'center', p: 4, flexGrow: 1 }}>

                  {/* CONTACT US BUTTON */}
                  <Button
                    variant="contained"
                    onClick={handleDialogOpen}
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 32,
                      px: 2.5,
                      py: 1,
                      fontSize: '0.95rem',
                      background: '#ffffff33',
                      backdropFilter: 'blur(4px)',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontFamily: 'Raleway',
                      '&:hover': { background: '#ffffff55' }
                    }}
                  >
                    Contact Us
                  </Button>

                  {/* IMAGE CAROUSEL */}
                  <Box sx={{ position: 'relative', width: '100%', mt: 6 }}>
                    <Button
                      onClick={prevVaultImage}
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: 0,
                        transform: 'translateY(-50%)',
                        minWidth: '40px',
                        color: '#fff',
                        fontSize: '2rem'
                      }}
                    >
                      ‹
                    </Button>

                    <motion.img
                      key={vaultIndex}
                      src={vaultImages[vaultIndex]}
                      alt="StudioVault Screenshot"
                      onLoad={() => {
                        updateBreak();
                        vaultCurrentLoadedRef.current = true;
                        maybeScheduleVaultAdvance();
                      }}
                      style={{
                        width: '100%',
                        borderRadius: '12px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
                      }}
                      initial={{ opacity: 0, x: 30 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5 }}
                    />

                    <Button
                      onClick={nextVaultImage}
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        right: 0,
                        transform: 'translateY(-50%)',
                        minWidth: '40px',
                        color: '#fff',
                        fontSize: '2rem'
                      }}
                    >
                      ›
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* StudioVault description below card */}
          <Box textAlign="center" sx={{ maxWidth: '750px', mx: 'auto', mt: 3, mb: 4 }}>
            <Typography
              variant="h6"
              sx={{
                color: '#fff',
                lineHeight: 1.5
              }}
            >
              A fast, modern Digital Asset Management system designed for storing,
              organizing and retrieving your digital assets.
            </Typography>
          </Box>

          {/* CTA BUTTONS */}
          <Box textAlign="center" sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              onClick={handleDialogOpen}
              sx={{
                px: 5,
                py: 1.8,
                fontSize: '1.2rem',
                borderColor: '#fff',
                color: '#fff',
                textTransform: 'none',
                fontFamily: 'Raleway',
                '&:hover': {
                  background: 'rgba(255,255,255,0.15)',
                  borderColor: '#fff',
                },
              }}
            >
              Contact a Distributor
            </Button>
            <Button
              variant="outlined"
              onClick={handleTestimonialsOpen}
              sx={{
                px: 5,
                py: 1.8,
                fontSize: '1.2rem',
                borderColor: '#fff',
                color: '#fff',
                textTransform: 'none',
                fontFamily: 'Raleway',
                '&:hover': {
                  background: 'rgba(255,255,255,0.15)',
                  borderColor: '#fff',
                },
              }}
            >
              Testamonials
            </Button>
          </Box>
        </Container>

        {/* DISTRIBUTOR DIALOG */}
        <Dialog open={openDialog} onClose={handleDialogClose} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ textAlign: 'center', color: '#1e3c72' }}>
            StudioFlow Distributors
          </DialogTitle>

          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              For enquiries, early access, or partnership opportunities, please
              contact your regional distributor:
            </Typography>

            <Typography variant="h6" sx={{ mt: 2 }}>South Africa</Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Warren Dixon —{' '}
              <a href="mailto:warren@lucky13technology.com" style={{ color: '#1e3c72', textDecoration: 'underline' }}>
                warren@lucky13technology.com
              </a>
            </Typography>

            <Typography variant="h6">United Kingdom</Typography>
            <Typography variant="body2">
              Mark Hodgson —{' '}
              <a href="mailto:mark@bluepinkblue.com" style={{ color: '#1e3c72', textDecoration: 'underline' }}>
                mark@bluepinkblue.com
              </a>
            </Typography>
          </DialogContent>

          <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
            <Button onClick={handleDialogClose} variant="outlined" color="inherit">
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* TESTIMONIALS DIALOG */}
        <Dialog open={openTestimonials} onClose={handleTestimonialsClose} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ textAlign: 'center', color: '#1e3c72' }}>
            Client Testimonials
          </DialogTitle>

          <DialogContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Box
                component="img"
                src={`${process.env.PUBLIC_URL}/images/retail_logo.png`}
                alt="Retailability Logo"
                sx={{ width: 64, height: 64, borderRadius: '12px', objectFit: 'cover' }}
              />
              <Box>
                <Typography variant="h6" sx={{ color: '#1e3c72' }}>Retailability</Typography>
                <Typography variant="body2">
                  "The Studio Vault digital asset management system is  incredibly easy to use, making it simple to access, organise, and download our images whenever we need them." Arresha Ramsaywak - eCommerce Content Manager - Retailability
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Box
                component="img"
                src={`${process.env.PUBLIC_URL}/images/studiok_logo.jpg`}
                alt="StudioK Logo"
                sx={{ width: 64, height: 64, borderRadius: '12px', objectFit: 'cover' }}
              />
              <Box>
                <Typography variant="h6" sx={{ color: '#1e3c72' }}>StudioK</Typography>
                <Typography variant="body2">
                  Our clients love this app. Collaborating is so much easier. Everything’s in one place—no need to chase or resend files - Tricha Gouws, Studio Manager
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Box
                component="img"
                src={`${process.env.PUBLIC_URL}/images/kakiebos_logo.jpg`}
                alt="Kakiebos Logo"
                sx={{ width: 64, height: 64, borderRadius: '12px', objectFit: 'cover' }}
              />
              <Box>
                <Typography variant="h6" sx={{ color: '#1e3c72' }}>Kakiebos</Typography>
                <Typography variant="body2">
                  All my digital assets were easy to find and download—really user-friendly" - Steve Price, Head of Design, Khakibos
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                component="img"
                src={`${process.env.PUBLIC_URL}/images/kapow_logo.jpg`}
                alt="Kapow Comics Logo"
                sx={{ width: 64, height: 64, borderRadius: '12px', objectFit: 'cover' }}
              />
              <Box>
                <Typography variant="h6" sx={{ color: '#1e3c72' }}>Kapow Comics</Typography>
                <Typography variant="body2">
                  Before Studioflow, I spent hours digging through folder after folder just to find a single image. Now, it’s all just a click away. Studioflow has completely streamlined my way of working—everything’s easier to manage." Dale Brown, Kapow Comics eComm Manager
                </Typography>
              </Box>
            </Box>
          </DialogContent>

          <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
            <Button onClick={handleTestimonialsClose} variant="outlined" color="inherit">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default LandingPage;
